

import React, { useRef } from "react";
import { Box, Typography, Button, Grid, Container, Chip } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import blitzLogo from '../../../Components/globalAssets/logo_placeholder.png';
import { useTheme } from '@mui/material/styles';
import {
  Receipt,
  Schedule,
  CurrencyExchange,
  Paid,
} from "@mui/icons-material";
import routes from "../../../Config/routes";
import MagicCard from "../../../Components/MagicCard";
import AnimatedBeam from "../../../Components/AnimatedBeam";

const PaySection = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const theme = useTheme();
  
  // Refs for AnimatedBeam connections
  const hubContainerRef = useRef(null);
  const topCard1Ref = useRef(null);
  const topCard2Ref = useRef(null);
  const topCard3Ref = useRef(null);
  const centerLogoRef = useRef(null);
  const bottomCard1Ref = useRef(null);
  const bottomCard2Ref = useRef(null);
  const bottomCard3Ref = useRef(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const stats = [
    { value: "90%", label: "Faster Payments", color: "#f5576c" },
    { value: "24h", label: "Average Payout", color: "#667eea" },
    { value: "3.8x", label: "Faster Clearance", color: "#feca57" },
    { value: "97%", label: "Success Rate", color: "#48dbfb" }
  ];

  const features = [
    {
      title: "Brands & Agencies",
      icon: "🏢",
      avatars: ["B", "A"]
    },
    {
      title: "Payment Tools",
      icon: "💳",
      avatars: ["💰", "📊", "🔄"]
    },
    {
      title: "Finance & Operations",
      icon: "📈",
      avatars: ["F", "O"]
    }
  ];

  const creators = [
    {
      name: "Alex M.",
      role: "CREATORS",
      avatar: "A",
      flag: "🇺🇸",
      color: "#667eea"
    },
    {
      name: "Sarah K.",
      role: "INFLUENCERS",
      avatar: "S",
      flag: "🇬🇧",
      color: "#f5576c"
    },
    {
      name: "Jordan L.",
      role: "ARTISTS",
      avatar: "J",
      flag: "🇨🇦",
      color: "#43e97b"
    }
  ];

  const workflowSteps = [
    {
      icon: <Receipt sx={{ fontSize: { xs: 22, sm: 24, md: 28 } }} />,
      title: "Submit Invoice",
      text: "Upload invoices directly through our platform",
      color: "#f5576c",
      step: "01"
    },
    {
      icon: <Schedule sx={{ fontSize: { xs: 22, sm: 24, md: 28 } }} />,
      title: "Set Terms",
      text: "Choose Net 30/60 or instant payment",
      color: "#667eea",
      step: "02"
    },
    {
      icon: <CurrencyExchange sx={{ fontSize: { xs: 22, sm: 24, md: 28 } }} />,
      title: "Auto Convert",
      text: "Currency conversion at best rates",
      color: "#feca57",
      step: "03"
    },
    {
      icon: <Paid sx={{ fontSize: { xs: 22, sm: 24, md: 28 } }} />,
      title: "Get Paid",
      text: "Funds in your account within 24h",
      color: "#48dbfb",
      step: "04"
    }
  ];

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        py: { xs: 2, sm: 3, md: 4 },
        px: { sm: 3, md: 0 },
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Animated gradient orbs */}
      <Box sx={{
        position: "absolute",
        top: "15%",
        left: "-5%",
        width: { xs: "200px", sm: "300px", md: "500px" },
        height: { xs: "200px", sm: "300px", md: "500px" },
        background: "radial-gradient(circle, rgba(245,87,108,0.15) 0%, transparent 70%)",
        filter: "blur(60px)",
        animation: "float 15s ease-in-out infinite"
      }} />
      <Box sx={{
        position: "absolute",
        bottom: "20%",
        right: "-10%",
        width: { xs: "250px", sm: "400px", md: "600px" },
        height: { xs: "250px", sm: "400px", md: "600px" },
        background: "radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%)",
        filter: "blur(80px)",
        animation: "float 18s ease-in-out infinite reverse"
      }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 } }}>
            <motion.div variants={itemVariants}>
              <Chip 
                label="PAYMENTS" 
                sx={{ 
                  mb: { xs: 2, md: 3 },
                  background: "linear-gradient(135deg, #f5576c 0%, #f093fb 100%)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                  px: { xs: 1.5, md: 2 },
                  py: 0.5,
                  height: "auto"
                }} 
              />
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Moret Variable",
                  fontWeight: '600',
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "62px" },
                  color: "#0f172a",
                  mb: { xs: 2, md: 3 },
                  letterSpacing: "-0.02em"
                }}
              >
                BLITZ PAY
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant="h6"
                sx={{
                  color: "#475569",
                  mb: 2,
                  maxWidth: "800px",
                  mx: "auto",
                  lineHeight: 1.7,
                  fontSize: { xs: "0.9375rem", sm: "1.0625rem", md: "1.375rem" },
                  fontWeight: 300,
                  px: { xs: 1, sm: 2, md: 0 }
                }}
              >
                Revolutionize creator payments with <Box component="span" sx={{ color: "#f5576c", fontWeight: 600 }}>instant payouts</Box>, automated invoicing, and intelligent cash flow management.
              </Typography>
            </motion.div>
          </Box>

          {/* Stats Grid */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={6} md={3} key={index}>
                  <MagicCard
                    gradientFrom={stat.color}
                    gradientTo={stat.color}
                    gradientColor={stat.color}
                    gradientOpacity={0.06}
                    gradientSize={200}
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 4 },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1.5rem", sm: "1.875rem", md: "2.75rem" },
                        color: "#0f172a",
                        mb: 0.5,
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontWeight: 400,
                        fontSize: { xs: "0.6875rem", sm: "0.75rem", md: "0.875rem" },
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </MagicCard>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* Hub Section with Beams */}
          <Box
            ref={hubContainerRef}
            sx={{
              position: "relative",
              py: { xs: 4, sm: 6, md: 12 },
              mb: { xs: 4, sm: 6, md: 10 },
            }}
          >
            {/* Circular gradient background - more visible */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "100%", sm: "120%", md: "900px" },
                height: { xs: "100%", sm: "120%", md: "900px" },
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(200,235,200,0.6) 0%, rgba(210,245,210,0.35) 35%, rgba(220,250,220,0.15) 55%, transparent 75%)",
                pointerEvents: "none",
              }}
            />
            {/* Inner glow */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "70%", sm: "80%", md: "600px" },
                height: { xs: "70%", sm: "80%", md: "600px" },
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(180,230,180,0.3) 0%, transparent 60%)",
                pointerEvents: "none",
              }}
            />

            {/* Animated Beams - Only show on desktop */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <AnimatedBeam
                containerRef={hubContainerRef}
                fromRef={topCard1Ref}
                toRef={centerLogoRef}
                curvature={-50}
                pathWidth={3}
                pathColor="#c5e1a5"
                pathOpacity={0.4}
                gradientStartColor="#9be15d"
                gradientStopColor="#00c9a7"
                duration={4}
                delay={0}
              />
              <AnimatedBeam
                containerRef={hubContainerRef}
                fromRef={topCard2Ref}
                toRef={centerLogoRef}
                curvature={0}
                pathWidth={3}
                pathColor="#c5e1a5"
                pathOpacity={0.4}
                gradientStartColor="#9be15d"
                gradientStopColor="#00c9a7"
                duration={5}
                delay={0.5}
              />
              <AnimatedBeam
                containerRef={hubContainerRef}
                fromRef={topCard3Ref}
                toRef={centerLogoRef}
                curvature={50}
                pathWidth={3}
                pathColor="#c5e1a5"
                pathOpacity={0.4}
                gradientStartColor="#9be15d"
                gradientStopColor="#00c9a7"
                duration={4.5}
                delay={1}
              />
              {/* Bottom cards from center */}
              <AnimatedBeam
                containerRef={hubContainerRef}
                fromRef={centerLogoRef}
                toRef={bottomCard1Ref}
                curvature={50}
                pathWidth={3}
                pathColor="#c5e1a5"
                pathOpacity={0.4}
                gradientStartColor="#9be15d"
                gradientStopColor="#00c9a7"
                duration={4}
                delay={0.3}
                reverse
              />
              <AnimatedBeam
                containerRef={hubContainerRef}
                fromRef={centerLogoRef}
                toRef={bottomCard2Ref}
                curvature={0}
                pathWidth={3}
                pathColor="#c5e1a5"
                pathOpacity={0.4}
                gradientStartColor="#9be15d"
                gradientStopColor="#00c9a7"
                duration={5}
                delay={0.8}
                reverse
              />
              <AnimatedBeam
                containerRef={hubContainerRef}
                fromRef={centerLogoRef}
                toRef={bottomCard3Ref}
                curvature={-50}
                pathWidth={3}
                pathColor="#c5e1a5"
                pathOpacity={0.4}
                gradientStartColor="#9be15d"
                gradientStopColor="#00c9a7"
                duration={4.5}
                delay={1.3}
                reverse
              />
            </Box>

            {/* Top Feature Cards */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} justifyContent="center" sx={{ mb: { xs: 2, sm: 3, md: 6 }, position: "relative", zIndex: 2 }}>
              {features.map((feature, index) => {
                const cardRef = index === 0 ? topCard1Ref : index === 1 ? topCard2Ref : topCard3Ref;
                return (
                  <Grid item xs={12} sm={4} md={3} key={feature.title}>
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                    >
                      <Box ref={cardRef}>
                        <MagicCard
                          gradientFrom="#667eea"
                          gradientTo="#764ba2"
                          gradientOpacity={0.05}
                          sx={{
                            p: { xs: 2, sm: 2.5, md: 3 },
                            textAlign: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mb: { xs: 1.5, md: 2 } }}>
                            {feature.avatars.map((avatar, i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: { xs: 28, sm: 32, md: 36 },
                                  height: { xs: 28, sm: 32, md: 36 },
                                  borderRadius: "50%",
                                  background: "linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)",
                                  border: "2px solid white",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                                  marginLeft: i > 0 ? { xs: "-6px", md: "-8px" } : 0,
                                  position: "relative",
                                  zIndex: feature.avatars.length - i,
                                }}
                              >
                                {avatar}
                              </Box>
                            ))}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: "#0f172a",
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            }}
                          >
                            {feature.title}
                          </Typography>
                        </MagicCard>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>

            {/* Center BLITZ Logo - Premium text style */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 2, sm: 3, md: 6 }, position: "relative", zIndex: 2 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <Box ref={centerLogoRef}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <img 
                      src={blitzLogo} 
                      alt="Blitz" 
                      style={{ 
                        width: "100%", 
                        maxWidth: "200px",
                        height: "auto" 
                      }} 
                    />
                  </Box>
                </Box>
              </motion.div>
            </Box>

            {/* Bottom Creator Cards */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} justifyContent="center" sx={{ position: "relative", zIndex: 2 }}>
              {creators.map((creator, index) => {
                const cardRef = index === 0 ? bottomCard1Ref : index === 1 ? bottomCard2Ref : bottomCard3Ref;
                return (
                  <Grid item xs={12} sm={4} md={3} key={creator.name}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                    >
                      <Box ref={cardRef}>
                        <MagicCard
                          gradientFrom={creator.color}
                          gradientTo={creator.color}
                          gradientOpacity={0.05}
                          sx={{
                            p: { xs: 2, sm: 2.5, md: 3 },
                            textAlign: "center",
                            position: "relative",
                          }}
                        >
                          {/* Plus icon */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: { xs: 8, md: 12 },
                              right: { xs: 8, md: 12 },
                              width: { xs: 16, sm: 18, md: 20 },
                              height: { xs: 16, sm: 18, md: 20 },
                              borderRadius: "50%",
                              background: "#c8e6c9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                              fontWeight: 600,
                              color: "#2e7d32",
                            }}
                          >
                            +
                          </Box>

                          {/* Avatar */}
                          <Box
                            sx={{
                              width: { xs: 48, sm: 56, md: 64 },
                              height: { xs: 48, sm: 56, md: 64 },
                              borderRadius: "50%",
                              background: `linear-gradient(135deg, ${creator.color}30 0%, ${creator.color}10 100%)`,
                              border: `3px solid ${creator.color}40`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                              fontWeight: 600,
                              color: creator.color,
                              mx: "auto",
                              mb: { xs: 1, md: 1.5 },
                              position: "relative",
                            }}
                          >
                            {creator.avatar}
                            {/* Flag */}
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: -4,
                                right: -4,
                                fontSize: { xs: "1rem", md: "1.25rem" },
                              }}
                            >
                              {creator.flag}
                            </Box>
                          </Box>

                          {/* Role label */}
                          <Typography
                            sx={{
                              fontSize: { xs: "0.5625rem", sm: "0.6rem", md: "0.65rem" },
                              fontWeight: 500,
                              color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: "0.1em",
                              mb: 0.5,
                            }}
                          >
                            {creator.role}
                          </Typography>

                          {/* Name */}
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: "#0f172a",
                              fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                            }}
                          >
                            {creator.name}
                          </Typography>
                        </MagicCard>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Streamlined Payment Workflow */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                  fontFamily: "Moret Variable",
                  fontWeight: '600',
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "3rem", lg: "62px" }
                }}
              >
                Streamlined Payment Workflow
              </Typography>
            </Box>

            {/* Modern Step Cards */}
            <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                {workflowSteps.map((step, index) => (
                  <Grid item xs={6} sm={6} md={3} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          p: { xs: 2, sm: 2.5, md: 3 },
                          pt: { xs: 3, sm: 3.5, md: 4 },
                          height: "100%",
                          background: "white",
                          borderRadius: { xs: "14px", sm: "16px", md: "20px" },
                          border: "1px solid rgba(0,0,0,0.04)",
                          boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                          overflow: "hidden",
                          "&:hover": {
                            transform: { xs: "none", sm: "translateY(-8px)" },
                            boxShadow: { xs: "0 4px 24px rgba(0,0,0,0.04)", sm: "0 20px 40px rgba(0,0,0,0.08)" },
                            "& .step-number": {
                              transform: { xs: "none", sm: "scale(1.1)" },
                              color: { xs: "rgba(0,0,0,0.04)", sm: step.color },
                            },
                            "& .step-icon": {
                              background: { xs: `${step.color}12`, sm: step.color },
                              color: { xs: step.color, sm: "white" },
                            },
                          },
                        }}
                      >
                        {/* Step Number - Large watermark style */}
                        <Typography
                          className="step-number"
                          sx={{
                            position: "absolute",
                            top: { xs: 8, sm: 10, md: 12 },
                            right: { xs: 10, sm: 12, md: 16 },
                            fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
                            fontWeight: 800,
                            color: "rgba(0,0,0,0.04)",
                            lineHeight: 1,
                            transition: "all 0.3s ease",
                          }}
                        >
                          {step.step}
                        </Typography>

                        {/* Icon */}
                        <Box
                          className="step-icon"
                          sx={{
                            width: { xs: 36, sm: 40, md: 48 },
                            height: { xs: 36, sm: 40, md: 48 },
                            borderRadius: { xs: "10px", sm: "12px", md: "14px" },
                            background: `${step.color}12`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: step.color,
                            mb: { xs: 1.5, sm: 2, md: 2.5 },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {step.icon}
                        </Box>

                        {/* Title */}
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#0f172a",
                            fontSize: { xs: "0.8125rem", sm: "0.9rem", md: "1.05rem" },
                            mb: { xs: 0.5, md: 1 },
                          }}
                        >
                          {step.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                          sx={{
                            color: "#64748b",
                            fontSize: { xs: "0.6875rem", sm: "0.75rem", md: "0.85rem" },
                            lineHeight: 1.6,
                            display: { xs: "-webkit-box", md: "block" },
                            WebkitLineClamp: { xs: 2, md: "unset" },
                            WebkitBoxOrient: "vertical",
                            overflow: { xs: "hidden", md: "visible" },
                          }}
                        >
                          {step.text}
                        </Typography>

                        {/* Progress indicator - shows connection to next */}
                        {index < workflowSteps.length - 1 && (
                          <Box
                            sx={{
                              display: { xs: "none", md: "flex" },
                              position: "absolute",
                              top: "50%",
                              right: -18,
                              transform: "translateY(-50%)",
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: "white",
                              border: "1px solid rgba(0,0,0,0.06)",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: `linear-gradient(135deg, ${step.color}, ${workflowSteps[index + 1].color})`,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Comparison Cards */}
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
              {[
                { 
                  title: "Standard", 
                  time: "2-5 days", 
                  fee: "2-3%", 
                  impact: "Moderate",
                  isHighlighted: false 
                },
                { 
                  title: "BlitzPay", 
                  time: "<24h", 
                  fee: "4-10%", 
                  impact: "Positive",
                  isHighlighted: true 
                },
                { 
                  title: "Net 30", 
                  time: "30+ days", 
                  fee: "0%", 
                  impact: "Negative",
                  isHighlighted: false 
                },
              ].map((plan, index) => (
                <Grid item xs={12} sm={4} md={4} key={plan.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
                  >
                    <Box
                      sx={{
                        p: { xs: 2, sm: 2.5, md: 4 },
                        borderRadius: { xs: "14px", sm: "16px", md: "20px" },
                        background: plan.isHighlighted 
                          ? "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)" 
                          : "white",
                        border: plan.isHighlighted 
                          ? "1px solid rgba(15,23,42,0.12)" 
                          : "1px solid rgba(0,0,0,0.05)",
                        boxShadow: plan.isHighlighted 
                          ? "0 8px 32px rgba(15,23,42,0.1)" 
                          : "0 2px 12px rgba(0,0,0,0.04)",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: { xs: "none", sm: "translateY(-6px)" },
                          boxShadow: plan.isHighlighted 
                            ? { xs: "0 8px 32px rgba(15,23,42,0.1)", sm: "0 20px 50px rgba(15,23,42,0.15)" }
                            : { xs: "0 2px 12px rgba(0,0,0,0.04)", sm: "0 12px 32px rgba(0,0,0,0.08)" },
                        },
                      }}
                    >
                      {plan.isHighlighted && (
                        <Chip
                          label="RECOMMENDED"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: { xs: 10, md: 16 },
                            right: { xs: 10, md: 16 },
                            background: "transparent",
                            color: "#667eea",
                            fontWeight: 700,
                            fontSize: { xs: "0.5rem", md: "0.6rem" },
                            height: { xs: 18, md: 22 },
                            border: "1px solid #667eea",
                            "& .MuiChip-label": {
                              px: { xs: 0.75, md: 1 },
                            },
                          }}
                        />
                      )}
                      
                      {/* Title */}
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                          color: "#0f172a",
                          mb: { xs: 2, md: 3 },
                        }}
                      >
                        {plan.title}
                      </Typography>

                      {/* Payment Time - Hero stat */}
                      <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                            color: "#0f172a",
                            lineHeight: 1,
                          }}
                        >
                          {plan.time}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: "0.6875rem", sm: "0.75rem", md: "0.8rem" },
                            color: "#94a3b8",
                            mt: { xs: 0.5, md: 1 },
                          }}
                        >
                          Payment Time
                        </Typography>
                      </Box>

                      {/* Fee */}
                      <Box sx={{ 
                        py: { xs: 1.5, md: 2 }, 
                        px: { xs: 1.5, md: 2 }, 
                        borderRadius: { xs: "10px", md: "12px" }, 
                        background: "rgba(0,0,0,0.025)",
                        mb: { xs: 1.5, md: 2 },
                      }}>
                        <Typography sx={{ 
                          fontSize: { xs: "0.5625rem", sm: "0.625rem", md: "0.7rem" }, 
                          color: "#94a3b8",
                          mb: 0.5,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}>
                          Fee
                        </Typography>
                        <Typography sx={{ 
                          fontWeight: 600, 
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                          color: "#0f172a" 
                        }}>
                          {plan.fee}
                        </Typography>
                      </Box>

                      {/* Cash Flow */}
                      <Box sx={{ 
                        py: { xs: 1.5, md: 2 }, 
                        px: { xs: 1.5, md: 2 }, 
                        borderRadius: { xs: "10px", md: "12px" }, 
                        background: "rgba(0,0,0,0.025)",
                      }}>
                        <Typography sx={{ 
                          fontSize: { xs: "0.5625rem", sm: "0.625rem", md: "0.7rem" }, 
                          color: "#94a3b8",
                          mb: 0.5,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}>
                          Cash Flow Impact
                        </Typography>
                        <Typography sx={{ 
                          fontWeight: 600, 
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                          color: plan.impact === "Positive" ? "#22c55e" : plan.impact === "Negative" ? "#ef4444" : "#0f172a"
                        }}>
                          {plan.impact}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* CTA */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(routes.onboard)}
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                  px: { xs: 3, sm: 5, md: 8 },
                  py: { xs: 1.5, sm: 2, md: 2.5 },
                  borderRadius: "50px",
                  boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.12)", md: "0 12px 40px rgba(0,0,0,0.15)" },
                  position: "relative",
                  overflow: "hidden",
                  textTransform: "none",
                  width: { xs: "100%", sm: "auto" },
                  '&::before': {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s",
                  },
                  '&:hover': {
                    bgcolor: theme.palette.primary.light,
                    transform: { xs: "none", sm: "translateY(-4px) scale(1.02)" },
                    boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.15)", md: "0 16px 56px rgba(0,0,0,0.2)" },
                    '&::before': {
                      left: '100%'
                    }
                  },
                  transition: "all 0.3s ease"
                }}
              >
                <span style={{ position: "relative", zIndex: 1 }}>
                  Start Getting Paid Instantly
                </span>
              </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PaySection;